import { normalize } from 'normalizr'

class ListFetchResult {
  constructor (totalNrOfItems, items) {
    if (!items || !Array.isArray(items)) {
      throw new Error(`[ListFetchResult] Items should be an array: received ${items}`)
    }

    this.totalNrOfItems = totalNrOfItems
    this.items = items
  }

  /**
   * Converts list fetch result into a normalized form, used by different store types: data-stores and experience-stores.
   * @param normalizationSchema {object}: normalization schema of `this.items`
   * @param RESTItemsToStoreItems {object}: conversion mapping for converting the normalized REST items into store-items.
   * The mapping keys are the entity names and the values are their associated REST-to-store-item handlers.
   * @returns {{result: string[], entities: {}, totalNrOfItems: number}}:
   *  - `result` contains the ids of `this.items`
   *  - `entities` contains the normalized entities mapped by their "entity name"
   *  - `totalNrOfItems` contains the total number of items on the server(`this.items`). It is useful in list management.
   */
  toStoreValues (normalizationSchema, RESTItemsToStoreItems) {
    const normalizedData = normalize(this.items, [normalizationSchema])
    return ({
      totalNrOfItems: this.totalNrOfItems,
      result: normalizedData.result,
      entities: Object
        .keys(normalizedData.entities)
        // convert entities(normalized rest items) to store items
        .reduce(
          (entities, entityCategoryName) => {
            if (normalizedData.entities[entityCategoryName]) {
              // if entities from this category were extracted during normalization, process them
              entities[entityCategoryName] = typeof RESTItemsToStoreItems[entityCategoryName] === 'function'
                // convert entities to store items if a converter exists for this entity category
                ? Object
                  .keys(normalizedData.entities[entityCategoryName])
                  .reduce(
                    (convertedEntities, entityId) => {
                      convertedEntities[entityId] = RESTItemsToStoreItems[entityCategoryName](
                        normalizedData.entities[entityCategoryName][entityId]
                      ).toJSON()
                      return convertedEntities
                    },
                    {}
                  )
                // return entities as they are if no converter exists for this entity category
                : normalizedData.entities[entityCategoryName]
            }

            return entities
          },
          {}
        )
    })
  }

  /**
   * Build from HTTP list response (Axios-style).
   * @param axiosResponse {{ data: { totalCount: number, items: object[] } }}
   * @returns {ListFetchResult}
   */
  static FromAxiosResponse (axiosResponse) {
    const { totalCount, items } = axiosResponse.data
    return new ListFetchResult(totalCount, items)
  }

  /**
   * Build from a list payload (e.g. socket list result).
   * Accepts either { totalCount, items } or { data: { totalCount, items } }.
   * Use this for real-time/socket list responses instead of FromAxiosResponse.
   * @param payload {{ totalCount?: number, items?: object[] } | { data: { totalCount: number, items: object[] } }}
   * @returns {ListFetchResult}
   */
  static fromListPayload (payload) {
    if (Array.isArray(payload) && payload.length && typeof payload[0] === 'object' && payload[0]?.id != null) {
      return new ListFetchResult(payload.length, payload)
    }
    const data = payload && payload.data !== undefined ? payload.data : payload
    if (Array.isArray(data) && data.length && typeof data[0] === 'object' && data[0]?.id != null) {
      return new ListFetchResult(data.length, data)
    }
    const { totalCount, items } = data || {}
    const total = typeof totalCount === 'number' ? totalCount : (items?.length ?? 0)
    return new ListFetchResult(total, items ?? [])
  }
}

export default ListFetchResult
